<template>
  <v-dialog
    v-model="dialog"
    persistent
    :max-width="$vuetify.breakpoint.mobile ? '98vw' : '960'"
    content-class="dds-report-payment-dialog"
  >
    <v-card style="position: relative;">
      <v-toolbar color="primary" elevation="0">
        <v-toolbar-title class="white--text">{{ $t('dueDiligence.reportPayment') }}</v-toolbar-title>
        <v-spacer></v-spacer>
        <v-btn color="white" icon @click="closeModal" :disabled="submitting">
          <v-icon style="color:white !important">mdi-close</v-icon>
        </v-btn>
      </v-toolbar>

      <v-overlay :value="submitting" absolute opacity="0.55" z-index="6">
        <div class="d-flex flex-column align-center white--text">
          <v-progress-circular indeterminate color="white" size="48" class="mb-3"></v-progress-circular>
          <div class="text-center">{{ uploading ? $t('dueDiligence.uploadingReceipt') : $t('dueDiligence.submittingPayment') }}</div>
        </div>
      </v-overlay>

      <v-card-text class="pa-0 report-payment-scroll">
        <div class="report-payment-document">
          <div class="payment-top">
            <div>
              <div class="payment-brand">{{ issuerName }}</div>
              <div class="payment-subtitle">{{ $t('dueDiligence.reportPaymentSubtitle') }}</div>
            </div>
            <div class="payment-amount-chip">
              <span class="chip-label">{{ $t('dueDiligence.totalAmountPayable') }}</span>
              <span class="chip-value">{{ formattedAmount }}</span>
            </div>
          </div>

          <!-- Payment Summary -->
          <section class="payment-section">
            <h3 class="section-title">{{ $t('dueDiligence.paymentInformation') }}</h3>
            <div class="section-grid">
              <div class="field">
                <span class="label">{{ $t('dueDiligence.invoiceNumber') }}</span>
                <span class="value">{{ invoiceNumber }}</span>
              </div>
              <div class="field">
                <span class="label">{{ $t('dueDiligence.ddsReferenceNo') }}</span>
                <span class="value">{{ ddsReference }}</span>
              </div>
              <div class="field">
                <span class="label">{{ $t('deforestation.product') }}</span>
                <span class="value">{{ productName }}</span>
              </div>
              <div class="field">
                <span class="label">{{ $t('dueDiligence.quantityNetMass') }}</span>
                <span class="value">{{ formattedNetMass }} {{ $t('dueDiligence.unitTon') }}</span>
              </div>
              <div class="field">
                <span class="label">{{ $t('dueDiligence.pricePerTon') }}</span>
                <span class="value">{{ formattedPricePerTon }}</span>
              </div>
              <div class="field">
                <span class="label">{{ $t('dueDiligence.paymentAmountDue') }}</span>
                <span class="value amount-emphasis">{{ formattedAmount }}</span>
              </div>
            </div>
          </section>

          <!-- Bank Details (toggle via showDdsPaymentBankDetails in utils.js) -->
          <section v-if="ddsPaymentBankDetailsVisible" class="payment-section">
            <h3 class="section-title">{{ $t('dueDiligence.bankDetails') }}</h3>
            <p class="section-hint mb-3">{{ $t('dueDiligence.bankTransferPaymentInstructions') }}</p>
            <div class="bank-box">
              <div class="bank-row">
                <span class="label">{{ $t('dueDiligence.bankName') }}</span>
                <span class="value">{{ bankDetails.bankName || '-' }}</span>
              </div>
              <div class="bank-row">
                <span class="label">{{ $t('dueDiligence.accountName') }}</span>
                <span class="value">{{ bankDetails.accountName || '-' }}</span>
              </div>
              <div class="bank-row">
                <span class="label">{{ $t('dueDiligence.accountNumber') }}</span>
                <span class="value">{{ bankDetails.accountNumber || '-' }}</span>
              </div>
            </div>
          </section>

          <!-- Upload Receipt -->
          <section class="payment-section highlight">
            <h3 class="section-title">{{ $t('dueDiligence.uploadReceipt') }}</h3>
            <p class="section-hint mb-3">{{ $t('dueDiligence.uploadReceiptHint') }}</p>

            <div
              v-if="!receiptFile"
              class="receipt-dropzone"
              :class="{
                'receipt-dropzone--active': dragging,
                'receipt-dropzone--disabled': uploading || submitting,
              }"
              @dragenter.prevent="onDragEnter"
              @dragover.prevent="onDragOver"
              @dragleave.prevent="onDragLeave"
              @drop.prevent="onDrop"
            >
              <div class="receipt-dropzone__content">
                <v-icon size="44" :color="dragging ? 'primary' : 'grey lighten-1'">mdi-tray-arrow-up</v-icon>
                <div class="receipt-dropzone__title primary--text">
                  {{ $t('dueDiligence.uploadReceiptPlaceholder') }}
                </div>
                <div class="receipt-dropzone__subtitle">
                  {{ $t('farm.dropHere') }}
                </div>
                <div class="receipt-dropzone__formats text-caption text--secondary mt-2">
                  PDF, JPG, PNG
                </div>
              </div>
              <input
                ref="receiptInput"
                type="file"
                class="receipt-dropzone__input"
                accept="image/*,.pdf"
                :disabled="uploading || submitting"
                @change="onReceiptInputChange"
              />
            </div>

            <div v-else class="receipt-file-card">
              <div class="receipt-file-card__icon">
                <v-icon color="primary">{{ receiptFileIcon }}</v-icon>
              </div>
              <div class="receipt-file-card__meta">
                <div class="receipt-file-card__name">{{ receiptFile.name }}</div>
                <div class="receipt-file-card__size text-caption text--secondary">
                  {{ formatFileSize(receiptFile.size) }}
                </div>
              </div>
              <v-btn
                icon
                small
                :disabled="uploading || submitting"
                @click="clearReceiptFile"
              >
                <v-icon small color="error">mdi-close</v-icon>
              </v-btn>
            </div>

            <v-textarea
              v-model="transactionNote"
              :label="$t('dueDiligence.paymentTransactionNote')"
              :placeholder="$t('dueDiligence.paymentTransactionNotePlaceholder')"
              outlined
              rows="2"
              :disabled="submitting"
              class="mt-2"
            ></v-textarea>
          </section>
        </div>
      </v-card-text>

      <v-card-actions class="px-6 pb-4">
        <v-spacer></v-spacer>
        <v-btn outlined color="primary" @click="closeModal" :disabled="submitting">
          {{ $t('cancel') }}
        </v-btn>
        <v-btn
          depressed
          color="primary"
          @click="submitPayment"
          :loading="submitting"
          :disabled="!receiptFile || uploading"
        >
          {{ $t('dueDiligence.submitPayment') }}
        </v-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>

<script>
import S3UploadService from '@/_services/S3UploadService';
import { showDdsPaymentBankDetails as isDdsPaymentBankDetailsEnabled } from '@/utils';

export default {
  name: 'ReportPaymentModal',
  props: {
    dialog: {
      type: Boolean,
      required: true,
    },
    payment: {
      type: Object,
      default: null,
    },
    item: {
      type: Object,
      default: null,
    },
    bankDetails: {
      type: Object,
      default: () => ({
        bankName: '',
        accountName: '',
        accountNumber: '',
      }),
    },
    issuerName: {
      type: String,
      default: 'PT Surveyor Indonesia',
    },
  },
  data() {
    return {
      ddsPaymentBankDetailsVisible: isDdsPaymentBankDetailsEnabled(),
      receiptFile: null,
      transactionNote: '',
      uploading: false,
      submitting: false,
      dragging: false,
      dragDepth: 0,
    };
  },
  computed: {
    currency() {
      return this.payment?.currency || 'IDR';
    },
    formattedAmount() {
      if (!this.payment || this.payment.amount == null) return '-';
      return `${this.currency} ${Number(this.payment.amount).toLocaleString()}`.trim();
    },
    invoiceNumber() {
      if (this.payment?.invoice_number) return this.payment.invoice_number;
      if (this.item?.id && this.payment?.id) return `INV-${this.item.id}-${this.payment.id}`;
      if (this.item?.id) return `INV-${this.item.id}`;
      return '-';
    },
    ddsReference() {
      const eudr = this.item?.EUDRReferenceNumber;
      const internal = this.item?.internalReferenceNumber;
      if (eudr && internal) return `${eudr} / ${internal}`;
      return eudr || internal || '-';
    },
    productName() {
      const detail = this.item?.product_detail;
      if (!detail) return this.item?.product || '-';
      return detail.code ? `${detail.code} - ${detail.name}` : detail.name;
    },
    netMassTons() {
      if (this.payment?.net_mass_tons_snapshot != null) {
        return Number(this.payment.net_mass_tons_snapshot);
      }
      const kg = this.item?.productNetMass;
      if (kg == null || isNaN(kg)) return null;
      return Number(kg) / 1000;
    },
    formattedNetMass() {
      if (this.netMassTons == null) return '-';
      return this.netMassTons.toLocaleString(undefined, { maximumFractionDigits: 4 });
    },
    pricePerTon() {
      if (this.payment?.price_per_ton_snapshot != null) {
        return Number(this.payment.price_per_ton_snapshot);
      }
      if (this.payment?.amount != null && this.netMassTons) {
        return Number(this.payment.amount) / this.netMassTons;
      }
      return null;
    },
    formattedPricePerTon() {
      if (this.pricePerTon == null) return '-';
      return `${this.currency} ${this.pricePerTon.toLocaleString()}`.trim();
    },
    receiptFileIcon() {
      const name = this.receiptFile?.name || '';
      return name.toLowerCase().endsWith('.pdf') ? 'mdi-file-pdf-box' : 'mdi-file-image';
    },
  },
  methods: {
    onDragEnter() {
      if (this.uploading || this.submitting) return;
      this.dragDepth += 1;
      this.dragging = true;
    },
    onDragOver() {
      if (this.uploading || this.submitting) return;
      this.dragging = true;
    },
    onDragLeave() {
      if (this.uploading || this.submitting) return;
      this.dragDepth = Math.max(0, this.dragDepth - 1);
      if (this.dragDepth === 0) this.dragging = false;
    },
    onDrop(event) {
      if (this.uploading || this.submitting) return;
      this.dragging = false;
      this.dragDepth = 0;
      const file = event.dataTransfer?.files?.[0];
      if (file) this.setReceiptFile(file);
    },
    onReceiptInputChange(event) {
      const file = event.target?.files?.[0];
      if (file) this.setReceiptFile(file);
      if (this.$refs.receiptInput) this.$refs.receiptInput.value = '';
    },
    setReceiptFile(file) {
      if (!this.isAllowedReceiptFile(file)) {
        this.$emit('error', new Error(`${this.$t('dueDiligence.uploadReceiptHint')}`));
        return;
      }
      this.receiptFile = file;
    },
    isAllowedReceiptFile(file) {
      if (!file?.name) return false;
      const lowerName = file.name.toLowerCase();
      if (lowerName.endsWith('.pdf')) return true;
      return (file.type || '').startsWith('image/');
    },
    clearReceiptFile() {
      this.receiptFile = null;
      if (this.$refs.receiptInput) this.$refs.receiptInput.value = '';
    },
    formatFileSize(bytes) {
      if (!bytes && bytes !== 0) return '';
      if (bytes < 1024) return `${bytes} B`;
      if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
      return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
    },
    closeModal() {
      this.resetForm();
      this.$emit('close');
    },
    resetForm() {
      this.receiptFile = null;
      this.transactionNote = '';
      this.uploading = false;
      this.submitting = false;
      this.dragging = false;
      this.dragDepth = 0;
      if (this.$refs.receiptInput) this.$refs.receiptInput.value = '';
    },
    async submitPayment() {
      if (!this.receiptFile || this.submitting) return;

      this.submitting = true;
      try {
        this.uploading = true;
        const { key } = await S3UploadService.singleUpload(this.receiptFile, 'dds-payment-receipts');
        this.uploading = false;

        // Keep loading until parent finishes ReportDdsPayment, then close/reset via done().
        this.$emit('confirm', {
          receiptS3Key: key,
          transactionNote: this.transactionNote?.trim() || undefined,
          done: (error) => {
            this.submitting = false;
            this.uploading = false;
            if (!error) {
              this.closeModal();
            }
          },
        });
      } catch (error) {
        this.uploading = false;
        this.submitting = false;
        this.$emit('error', error);
      }
    },
  },
  watch: {
    dialog(newVal) {
      if (!newVal) {
        this.resetForm();
      }
    },
  },
};
</script>

<style scoped>
.report-payment-scroll {
  max-height: 75vh;
  overflow-y: auto;
  background: #fff;
}

.report-payment-document {
  background: #fff;
  width: 100%;
  max-width: 100%;
  margin: 0;
  padding: 24px 28px;
  color: #1a1a1a;
  box-sizing: border-box;
}

.payment-top {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  margin-bottom: 24px;
  border-bottom: 2px solid #0d47a1;
  padding-bottom: 16px;
  gap: 16px;
}

.payment-brand {
  font-size: 22px;
  font-weight: 700;
  color: #0d47a1;
}

.payment-subtitle {
  font-size: 13px;
  color: #555;
  margin-top: 4px;
}

.payment-amount-chip {
  background: #e8f5e9;
  border: 1px solid #c8e6c9;
  border-radius: 8px;
  padding: 10px 14px;
  text-align: right;
  min-width: 160px;
}

.chip-label {
  display: block;
  font-size: 11px;
  color: #2e7d32;
  margin-bottom: 2px;
}

.chip-value {
  display: block;
  font-size: 18px;
  font-weight: 700;
  color: #1b5e20;
}

.payment-section {
  margin-bottom: 20px;
}

.section-title {
  font-size: 13px;
  text-transform: uppercase;
  letter-spacing: 0.04em;
  color: #0d47a1;
  margin: 0 0 10px;
  border-bottom: 1px solid #e0e0e0;
  padding-bottom: 6px;
}

.section-hint {
  font-size: 13px;
  color: #666;
  margin: 0;
  line-height: 1.5;
}

.section-grid {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 12px 24px;
}

.field {
  display: flex;
  flex-direction: column;
}

.label {
  font-size: 11px;
  color: #666;
  margin-bottom: 2px;
}

.value {
  font-size: 14px;
  font-weight: 600;
}

.amount-emphasis {
  color: #0d47a1;
  font-size: 16px;
}

.bank-box {
  background: #f5f8fc;
  border: 1px solid #d6e0ef;
  border-radius: 8px;
  padding: 14px 16px;
}

.bank-row {
  display: flex;
  justify-content: space-between;
  align-items: baseline;
  gap: 16px;
  padding: 6px 0;
}

.bank-row + .bank-row {
  border-top: 1px solid #e3ebf5;
}

.highlight {
  background: #f8fafc;
  padding: 14px;
  border-radius: 8px;
  border: 1px solid #e8eef5;
}

.receipt-dropzone {
  position: relative;
  width: 100%;
  min-height: 168px;
  border: 2px dashed #b0bec5;
  border-radius: 12px;
  background: #fff;
  cursor: pointer;
  transition: border-color 0.2s ease, background-color 0.2s ease, box-shadow 0.2s ease;
  overflow: hidden;
}

.receipt-dropzone:hover:not(.receipt-dropzone--disabled) {
  border-color: #0d47a1;
  background: #f3f8ff;
}

.receipt-dropzone--active {
  border-color: #0d47a1;
  background: #e8f1ff;
  box-shadow: inset 0 0 0 1px rgba(13, 71, 161, 0.15);
}

.receipt-dropzone--disabled {
  opacity: 0.6;
  pointer-events: none;
  cursor: not-allowed;
}

.receipt-dropzone__content {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  text-align: center;
  padding: 28px 20px;
  pointer-events: none;
}

.receipt-dropzone__title {
  margin-top: 10px;
  font-size: 15px;
  font-weight: 600;
}

.receipt-dropzone__subtitle {
  margin-top: 4px;
  color: #757575;
  font-size: 14px;
}

.receipt-dropzone__input {
  position: absolute;
  inset: 0;
  width: 100%;
  height: 100%;
  opacity: 0;
  cursor: pointer;
}

.receipt-file-card {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 12px 14px;
  border: 1px solid #d6e0ef;
  border-radius: 8px;
  background: #fff;
}

.receipt-file-card__icon {
  flex-shrink: 0;
}

.receipt-file-card__meta {
  flex: 1;
  min-width: 0;
}

.receipt-file-card__name {
  font-size: 14px;
  font-weight: 600;
  word-break: break-word;
}

@media (max-width: 600px) {
  .report-payment-document {
    padding: 16px;
  }

  .payment-top {
    flex-direction: column;
  }

  .payment-amount-chip {
    text-align: left;
    width: 100%;
  }

  .section-grid {
    grid-template-columns: 1fr;
  }

  .bank-row {
    flex-direction: column;
    gap: 2px;
  }
}
</style>
